/**
 * Sonuç sayfası giriş noktası — `results.js`.
 *
 * Arama çubuğundan (`widget.js`) ayrı dosya: yalnız sonuç sayfasında yüklenir,
 * çubuğun her sayfaya binen boyutunu büyütmez. İstemci ve tema değişkenleri
 * aynı klasörden paylaşılır.
 *
 * ```html
 * <div id="arama-sonuclari"></div>
 * <script src=".../widget/ai-search/v1/results.js"
 *         data-search-key="…" data-mount="#arama-sonuclari"
 *         data-currency="TRY" defer></script>
 * ```
 *
 * Arama çubuğu bu sayfaya `data-search-url="/arama?q={q}"` ile yollar.
 * `window.SendheapResults` altına yerleşir (çubuğun `SendheapSearch`'üyle
 * çakışmaz; ikisi aynı sayfada birlikte yüklenebilir).
 */

import {
  createSearchClient,
  type ISearchClient,
  type ISearchClientOptions,
} from "./client";
import {
  createResultsUi,
  type IResultsTexts,
  type IResultsUi,
  type IResultsUiOptions,
  type TResultsTheme,
} from "./results-ui";

declare const __WIDGET_VERSION__: string;

export const version =
  typeof __WIDGET_VERSION__ === "string" ? __WIDGET_VERSION__ : "dev";

export type IResultsInitOptions = ISearchClientOptions &
  Omit<IResultsUiOptions, "client">;

export interface IResultsInstance extends IResultsUi {
  client: ISearchClient;
}

/**
 * Sonuç sayfasını kurar. Boş sorgu "tüm ürünler" demek (`listing` istemcinin
 * "en az karakter" kuralına tabi değil).
 */
export const init = (options: IResultsInitOptions): IResultsInstance => {
  const client = createSearchClient(options);
  const ui = createResultsUi({ ...options, client });
  return { ...ui, client };
};

export interface ISendheapResultsGlobal {
  version: string;
  init: (options: IResultsInitOptions) => IResultsInstance;
  /** `data-search-key` ile otomatik kurulan örnek; elle `init` çağrıldıysa `null`. */
  instance: IResultsInstance | null;
}

const api: ISendheapResultsGlobal = { version, init, instance: null };

/* --- Tek etiketli kurulum ------------------------------------------- */

/** `currentScript` yalnız script çalışırken dolu; modül yüklenirken yakala. */
const hostScript: HTMLScriptElement | null =
  typeof document !== "undefined" &&
  document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

const parseNum = (value: string | undefined) => {
  if (value === undefined || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const optionsFromDataset = (
  script: HTMLScriptElement,
): IResultsInitOptions | null => {
  const d = script.dataset;
  const searchKey = d.searchKey;
  if (!searchKey || !d.mount) return null;
  const theme: TResultsTheme | undefined =
    d.theme === "auto" || d.theme === "light" || d.theme === "dark"
      ? d.theme
      : undefined;
  const options: IResultsInitOptions = {
    searchKey,
    mount: d.mount,
    email: d.email || undefined,
    baseUrl: d.baseUrl || undefined,
    currency: d.currency || undefined,
    locale: d.locale || undefined,
    productUrl: d.productUrl || undefined,
    queryParam: d.queryParam || undefined,
    paramPrefix: d.paramPrefix,
    columns: parseNum(d.columns),
    theme,
    styles: d.styles === undefined ? undefined : !/^(false|0|no)$/i.test(d.styles),
  };
  (Object.keys(options) as (keyof IResultsInitOptions)[]).forEach((key) => {
    if (options[key] === undefined) delete options[key];
  });
  return options;
};

declare global {
  interface Window {
    SendheapResults?: ISendheapResultsGlobal;
  }
}

if (typeof window !== "undefined" && !window.SendheapResults) {
  window.SendheapResults = api;
  const options = hostScript ? optionsFromDataset(hostScript) : null;
  if (options) {
    const run = () => {
      try {
        api.instance = init(options);
      } catch (error) {
        console.error("[sendheap-search] sonuç sayfası kurulamadı:", error);
      }
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run, { once: true });
    } else {
      run();
    }
  }
}

export type { IResultsTexts, IResultsUi, IResultsUiOptions, TResultsTheme };
export default api;
